import { languages } from '@/config';
import { Plugins } from '@/plugins';
import AuthRedirectUtil from '@utils/AuthRedirectUtil';
import ConsentUtil from '@utils/ConsentUtil';
import ErrorUtil from '@utils/ErrorUtil';
import PosthogUtil from '@utils/PosthogUtil';
import ThemeUtil from '@utils/ThemeUtil';
import randomstring from 'randomstring';
import Vue from 'vue';
import VueI18n from 'vue-i18n';

// Resolve the closest supported locale from the browser's preferred languages.
function detectLang() {
    const supported = Object.keys(languages);
    const preferred = navigator.languages && navigator.languages.length
        ? navigator.languages
        : [navigator.language || navigator.userLanguage || 'en'];
    for (const tag of preferred) {
        if (!tag) continue;
        const lower = tag.toLowerCase();
        const exact = supported.find((locale) => locale.toLowerCase() === lower);
        if (exact) return exact;
        const base = lower.split('-')[0];
        const prefix = supported.find((locale) => locale.toLowerCase() === base);
        if (prefix) return prefix;
    }
    return 'en';
}

// Match in-progress ops without stealing another project's banner when project_id
// is omitted. connectionId uniquely identifies a sync; otherwise projectId is required.
function matchesInProgressOperation(o, { key, types, projectId, connectionId } = {}) {
    if (o.status !== 'progress') {
        return false;
    }
    if (key && o.key !== key) {
        return false;
    }
    if (types && !types.includes(o.type)) {
        return false;
    }
    if (connectionId != null) {
        if ((o.connectionId || null) !== connectionId) {
            return false;
        }
        if (projectId != null && o.projectId !== projectId) {
            return false;
        }
        return true;
    }
    // No connectionId: require projectId, and skip connection-scoped sync ops.
    if (projectId == null || o.projectId !== projectId) {
        return false;
    }
    if (o.connectionId) {
        return false;
    }
    return true;
}

function clearStoredLocation(state) {
    state.locationId = null;
    localStorage.setItem('location_id', null);
}

// Drop a location id left over from another account or project.
function clearLocationUnlessInProject(state, project) {
    if (state.locationId == null || state.locationId === 'null') {
        return;
    }
    if (!project || !Array.isArray(project.locations)) {
        return;
    }
    const belongs = project.locations.some(location => String(location.id) === String(state.locationId));
    if (!belongs) {
        clearStoredLocation(state);
    }
}

export default {

    setApiKey(state, apiKey) {
        state.apiKey = apiKey;
        localStorage.setItem('api_key', apiKey);
    },

    hideChecklist(state, projectId) {
        let checklists = state.checklists || [];
        if (!checklists.includes(projectId)) checklists.push(projectId);
        state.checklists = checklists;
        localStorage.setItem('checklists', JSON.stringify(checklists));
    },

    showChecklist(state, projectId) {
        let checklists = state.checklists || [];
        if (checklists.includes(projectId)) {
            checklists.splice(checklists.indexOf(projectId), 1);
        }
        state.checklists = checklists;
        localStorage.setItem('checklists', JSON.stringify(checklists));
    },

    setAlert(state, alert) {
        state.alerts.push({
            key: randomstring.generate(),
            message: alert.message,
            type: alert.type,
        });
    },

    setAlertSuccess(state, message) {
        state.alerts.push({
            key: randomstring.generate(),
            message: message,
            type: 'success',
        });
    },

    setAlertError(state, message) {
        state.alerts.push({
            key: randomstring.generate(),
            message: message,
            type: 'danger',
        });
    },

    setAlertWithError(state, error) {
        state.alerts.push({
            key: randomstring.generate(),
            message: ErrorUtil.error(error),
            type: 'danger'
        });
    },

    removeAlert(state, key) {
        let index = state.alerts.indexOf(state.alerts.find(a => a.key === key));
        state.alerts.splice(index, 1);
    },

    startOperation(state, operation) {
        const entry = {
            key: operation.key || randomstring.generate(),
            type: operation.type,
            status: 'progress',
            projectId: operation.projectId,
            connectionId: operation.connectionId || null,
            meta: operation.meta || null,
            message: null,
            dismissAfter: operation.dismissAfter || 5000,
        };
        const index = state.operations.findIndex(o => (
            o.status === 'progress' &&
            o.type === entry.type &&
            o.projectId === entry.projectId &&
            (o.connectionId || null) === (entry.connectionId || null)
        ));
        if (index >= 0) {
            Vue.set(state.operations, index, entry);
            return;
        }
        state.operations = [entry, ...state.operations];
    },

    completeOperation(state, { key, types, projectId, connectionId } = {}) {
        const index = state.operations.findIndex(o => matchesInProgressOperation(o, {
            key,
            types,
            projectId,
            connectionId,
        }));
        if (index < 0) {
            return;
        }
        Vue.set(state.operations, index, {
            ...state.operations[index],
            status: 'success',
            completedAt: Date.now(),
        });
    },

    failOperation(state, { key, types, projectId, connectionId, message } = {}) {
        // Only in-progress — matchesInProgressOperation ignores completed ops so a
        // delayed failure cannot overwrite success.
        const index = state.operations.findIndex(o => matchesInProgressOperation(o, {
            key,
            types,
            projectId,
            connectionId,
        }));
        if (index < 0) {
            return;
        }
        Vue.set(state.operations, index, {
            ...state.operations[index],
            status: 'failed',
            message: message || null,
            completedAt: Date.now(),
        });
    },

    dismissOperation(state, key) {
        state.operations = state.operations.filter(o => o.key !== key);
    },

    clearOperations(state) {
        state.operations = [];
    },

    loading(state, loading) {
        state.loading = loading;
    },

    setModal(state, show) {
        state.modal = show;
    },

    setModalTab(state, tab) {
        state.modalTab = tab;
    },

    setDropdown(state, id) {
        state.dropdown = id;
    },

    setExpandableSection(state, title) {
        state.expandableSection = title;
    },

    setTask(state, slug) {
        state.task = slug;
    },

    setConsent(state, categories) {
        state.consent = categories;
    },

    setShowCookieBanner(state, show) {
        state.showCookieBanner = show;
    },

    setInviteToken(state, inviteToken) {
        state.inviteToken = inviteToken;
        localStorage.setItem('invite_token', inviteToken);
    },

    unsetInviteToken(state) {
        state.inviteToken = null;
        localStorage.removeItem('invite_token')
    },

    setApp(state, app) {
        state.app = app;
    },

    setUser(state, user) {
        state.user = user;
        // Apply before app shell components render (AuthMiddleware boot).
        if (user) {
            ThemeUtil.setThemeForUser(user);
        }
        // Set selected project. Preserve setup completion when a later getUser
        // returns the same project without setup_completed_at (stale/partial).
        let project = user.selected_project || null;
        if (
            state.project &&
            project &&
            state.project.id === project.id &&
            state.project.setup_completed_at &&
            !project.setup_completed_at
        ) {
            project = {
                ...project,
                setup_completed_at: state.project.setup_completed_at,
                setup_step: state.project.setup_step != null
                    ? state.project.setup_step
                    : project.setup_step,
            };
            user.selected_project = project;
        }
        state.project = project;
        clearLocationUnlessInProject(state, project);
        // Set language.
        state.lang = (user.lang ?? 'en').replace('_', '-');
        if (user) {
            user.lang = state.lang;
        }
        localStorage.setItem('lang', state.lang);
        window.i18n.locale = state.lang;
        const agency = user.agency;
        if (agency && typeof agency === 'object' && !Array.isArray(agency)) {
            state.agency = agency;
            ThemeUtil.setThemeForAgency(agency);
        }
        // Identify.
        PosthogUtil.identify(user);
        ConsentUtil.onReady('analytics', () => {
            PosthogUtil.identify(user);
        });
        if (!ConsentUtil.isRequired()) {
            Plugins.identifyCrowdpower(user);
        } else {
            ConsentUtil.onReady('marketing', () => {
                Plugins.identifyCrowdpower(user);
            });
        }
    },

    setUserRelation(state, { key, value }) {
        if (state.user) {
            state.user[key] = value;
        }
    },

    setLang(state, lang) {
        state.lang = lang;
        localStorage.setItem('lang', state.lang);
        window.i18n.locale = lang;
    },

    setRef(state, ref) {
        const ref_at = Math.floor(Date.now() / 1000);

        if (!state.refFirst) {
            state.refFirst = ref;
            state.refFirstAt = ref_at;
            localStorage.setItem('ref_first', ref);
            localStorage.setItem('ref_first_at', ref_at);
        }

        state.refLast = ref;
        state.refLastAt = ref_at;
        localStorage.setItem('ref_last', ref);
        localStorage.setItem('ref_last_at', ref_at);
    },

    unsetRef(state) {
        state.refFirst = null;
        state.refFirstAt = null;
        state.refLast = null;
        state.refLastAt = null;
        localStorage.removeItem('ref_first');
        localStorage.removeItem('ref_first_at');
        localStorage.removeItem('ref_last');
        localStorage.removeItem('ref_last_at');
    },

    setTempLang(state) {
        const lang = detectLang();
        state.lang = lang;
        window.i18n.locale = lang;
    },

    setAgency(state, agency) {
        state.agency = agency;
        if (agency && typeof agency === 'object' && !Array.isArray(agency)) {
            ThemeUtil.setThemeForAgency(agency);
        }
    },

    setProject(state, project) {
        state.project = project;
        clearLocationUnlessInProject(state, project);
    },

    setLocationId(state, locationId) {
        state.locationId = locationId;
        localStorage.setItem('location_id', state.locationId);
    },

    setProjectRelation(state, { key, value }) {
        state.project[key] = value;
    },

    toggleSidebarState(state) {
        if (state.sidebarState === 'expanded') {
            state.sidebarState = 'collapsed';
        } else {
            state.sidebarState = 'expanded';
        }
        localStorage.setItem('sidebar_state', state.sidebarState);
    },

    logout(state) {
        // Unset the theme.
        if (state.user) {
            ThemeUtil.unsetThemeForUser(state.user);
            // Clear the agency.
            const domain = window.location.hostname.toLowerCase().replace('www.', '');
            if (domain === process.env.VUE_APP_DOMAIN) {
                state.agency = null;
            }
        }
        // Clear the user.
        state.user = null;
        // Clear the api key.
        state.apiKey = null;
        localStorage.removeItem('api_key');
        clearStoredLocation(state);
        state.operations = [];
        AuthRedirectUtil.clear();
        PosthogUtil.reset();
        if (typeof window.Beacon === 'function') {
            window.Beacon('logout');
        }
    },

}